import {
  Brush,
  Clock,
  FileText,
  Image,
  Loader2,
  Paperclip,
  Pen,
  Trash2,
  Undo,
  Video,
  X,
} from "lucide-react";
import { formatDuration, formatTime } from "@/utils/format";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Id } from "@cvx/_generated/dataModel";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@cvx/_generated/api";
import { useConvexMutation } from "@convex-dev/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

// Drawing Canvas Component
const DrawingCanvas = ({
  onSaveDrawing,
  onClearDrawing,
  videoFrame,
  onHideDrawing,
}: {
  onSaveDrawing: (svgString: string) => void;
  onClearDrawing: () => void;
  videoFrame?: string;
  onHideDrawing: () => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [paths, setPaths] = useState<
    Array<{ path: string; color: string; width: number }>
  >([]);
  const [currentPath, setCurrentPath] = useState<string>("");
  const [brushColor, setBrushColor] = useState("#ff0000");
  const [brushWidth, setBrushWidth] = useState(3);
  const [videoInfo, setVideoInfo] = useState<{
    src: string;
    currentTime: number;
    videoWidth: number;
    videoHeight: number;
    aspectRatio: number;
  } | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 400,
    height: 300,
  });

  // Parse video frame info and calculate canvas dimensions
  useEffect(() => {
    if (videoFrame) {
      try {
        const info = JSON.parse(videoFrame);
        setVideoInfo(info);

        // Calculate canvas dimensions based on video aspect ratio
        const aspectRatio =
          info.aspectRatio || info.videoWidth / info.videoHeight || 16 / 9;

        // Set max height to 300px and calculate width based on aspect ratio
        const maxHeight = 300;
        const maxWidth = 400;

        let canvasWidth, canvasHeight;

        if (aspectRatio > 1) {
          // Landscape video: width > height
          canvasWidth = Math.min(maxWidth, maxHeight * aspectRatio);
          canvasHeight = canvasWidth / aspectRatio;
        } else {
          // Portrait video: height > width
          canvasHeight = maxHeight;
          canvasWidth = canvasHeight * aspectRatio;
        }

        // Round to whole numbers to avoid sub-pixel rendering issues
        setCanvasDimensions({
          width: Math.round(canvasWidth),
          height: Math.round(canvasHeight),
        });

        console.log("Canvas dimensions calculated:", {
          width: Math.round(canvasWidth),
          height: Math.round(canvasHeight),
          aspectRatio,
        });
      } catch (e) {
        console.error("Failed to parse video frame info:", e);
        // Fallback to default dimensions
        setCanvasDimensions({ width: 400, height: 300 });
      }
    } else {
      console.log("No video frame provided");
      setVideoInfo(null);
      setVideoLoaded(false);
      setCanvasDimensions({ width: 400, height: 300 });
    }
  }, [videoFrame]);

  // Setup video element for background
  useEffect(() => {
    if (videoInfo && videoRef.current) {
      const video = videoRef.current;
      video.crossOrigin = "anonymous";

      // Set the source first
      video.src = videoInfo.src;

      const handleLoadedMetadata = () => {
        console.log(
          "Video metadata loaded, seeking to timestamp:",
          videoInfo.currentTime,
        );
        video.currentTime = videoInfo.currentTime;
      };

      const handleLoadedData = () => {
        console.log(
          "Video data loaded, ensuring correct timestamp:",
          videoInfo.currentTime,
        );
        video.currentTime = videoInfo.currentTime;
        setVideoLoaded(true);
      };

      const handleSeeked = () => {
        console.log("Video seeked to:", video.currentTime);
        setVideoLoaded(true);
      };

      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("seeked", handleSeeked);

      video.onerror = (error) => {
        console.error("Failed to load background video:", error);
      };

      return () => {
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("seeked", handleSeeked);
      };
    }
  }, [videoInfo]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);
    setCurrentPath(`M ${x} ${y}`);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCurrentPath((prev) => `${prev} L ${x} ${y}`);

    // Draw on canvas for immediate feedback
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (!isDrawing || !currentPath) return;

    setIsDrawing(false);
    setPaths((prev) => [
      ...prev,
      { path: currentPath, color: brushColor, width: brushWidth },
    ]);
    setCurrentPath("");

    // Reset canvas context
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (ctx) {
      ctx.beginPath();
    }
  };

  // Setup canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (ctx) {
      ctx.strokeStyle = brushColor;
      ctx.lineWidth = brushWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [brushColor, brushWidth]);

  // Redraw canvas with video background and paths
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw video background if available
    if (video && videoLoaded && video.readyState >= 2) {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        console.log("Drew video background to canvas");
      } catch (error) {
        console.log(
          "Could not draw video background (CORS), using overlay approach",
        );
      }
    }

    // Draw all completed paths
    paths.forEach(({ path, color, width }) => {
      const pathData = path.split(/[ML]/).filter(Boolean);
      if (pathData.length === 0) return;

      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = width;

      pathData.forEach((point, index) => {
        const [x, y] = point.trim().split(" ").map(Number);
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();
    });

    // Draw current path being drawn
    if (currentPath && isDrawing) {
      const pathData = currentPath.split(/[ML]/).filter(Boolean);
      if (pathData.length > 0) {
        ctx.beginPath();
        ctx.strokeStyle = brushColor;
        ctx.lineWidth = brushWidth;

        pathData.forEach((point, index) => {
          const [x, y] = point.trim().split(" ").map(Number);
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();
      }
    }
  }, [
    paths,
    currentPath,
    isDrawing,
    brushColor,
    brushWidth,
    videoLoaded,
    canvasDimensions,
  ]);

  const generateSVG = () => {
    if (paths.length === 0) return "";

    const svgPaths = paths
      .map(
        ({ path, color, width }) =>
          `<path d="${path}" stroke="${color}" stroke-width="${width}" fill="none" stroke-linecap="round" stroke-linejoin="round" />`,
      )
      .join("");

    return `<svg viewBox="0 0 ${canvasDimensions.width} ${canvasDimensions.height}" xmlns="http://www.w3.org/2000/svg">${svgPaths}</svg>`;
  };

  const handleSave = () => {
    const svg = generateSVG();
    if (svg) {
      onSaveDrawing(svg);
    }
  };

  const handleClear = () => {
    setPaths([]);
    setCurrentPath("");
    onClearDrawing();
  };

  const handleUndo = () => {
    setPaths((prev) => prev.slice(0, -1));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1">
          <label className="text-xs">Color:</label>
          <input
            type="color"
            value={brushColor}
            onChange={(e) => setBrushColor(e.target.value)}
            className="w-6 h-6 border rounded cursor-pointer"
          />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-xs">Size:</label>
          <input
            type="range"
            min="1"
            max="10"
            value={brushWidth}
            onChange={(e) => setBrushWidth(Number(e.target.value))}
            className="w-16"
          />
          <span className="text-xs w-4">{brushWidth}</span>
        </div>
        {videoLoaded && (
          <div className="text-xs text-green-600 flex items-center gap-1">
            ✓ Video frame loaded at{" "}
            {videoInfo ? formatTime(videoInfo.currentTime) : "unknown time"}
          </div>
        )}
        {videoInfo && !videoLoaded && (
          <div className="text-xs text-yellow-600 flex items-center gap-1">
            ⏳ Loading video frame at {formatTime(videoInfo.currentTime)}...
          </div>
        )}
      </div>

      <div className="relative">
        {/* Hidden video element for background */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none mx-auto"
          style={{
            width: `${canvasDimensions.width}px`,
            height: `${canvasDimensions.height}px`,
          }}
          muted
          playsInline
        />

        {/* Drawing canvas */}
        <canvas
          ref={canvasRef}
          width={canvasDimensions.width}
          height={canvasDimensions.height}
          className="border border-border rounded cursor-crosshair bg-transparent relative z-10 mx-auto block"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleUndo}
          disabled={paths.length === 0}
        >
          <Undo className="h-3 w-3 mr-1" />
          Undo
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleClear}
          disabled={paths.length === 0}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          Clear
        </Button>
        <Button size="sm" onClick={handleSave} disabled={paths.length === 0}>
          Save Drawing
        </Button>
        <Button size="sm" variant="destructive" onClick={onHideDrawing}>
          Hide Drawing
        </Button>
      </div>
    </div>
  );
};

export const PopupCommentForm = ({
  position,
  timestamp,
  selectedRange,
  onSubmit,
  onCancel,
  videoFrame,
}: {
  position: { x: number; y: number };
  timestamp: number;
  selectedRange?: { start: number; end: number };
  onSubmit: (
    content: string,
    attachments?: Array<{
      storageId: Id<"_storage">;
      name: string;
      type: string;
      size: number;
    }>,
    drawing?: string,
  ) => void;
  onCancel: () => void;
  videoFrame?: string;
}) => {
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<
    Array<{ file: File; storageId?: Id<"_storage">; uploading?: boolean }>
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showDrawing, setShowDrawing] = useState(false);
  const [drawing, setDrawing] = useState<string>("");
  const formRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [formPosition, setFormPosition] = useState(position);
  const { toast } = useToast();

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      const uploadedFiles = uploaded.map((upload, index) => ({
        storageId: (upload.response as { storageId: Id<"_storage"> }).storageId,
        name: upload.name,
        type: upload.type || "application/octet-stream",
        size: attachments[index]?.file.size || 0,
      }));

      // Update attachments with storage IDs
      setAttachments((prev) =>
        prev.map((att, index) => ({
          ...att,
          storageId: uploadedFiles[index]?.storageId,
          uploading: false,
        })),
      );
      setIsUploading(false);
    },
    onUploadError: () => {
      toast({
        title: "Error uploading files",
        description: "There was an error uploading your files",
        variant: "destructive",
      });
      setIsUploading(false);
      setAttachments((prev) =>
        prev.map((att) => ({ ...att, uploading: false })),
      );
    },
  });

  useEffect(() => {
    // Initial positioning based on timestamp, not mouse
    if (formRef.current) {
      const rect = formRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = position.x;
      let adjustedY = position.y;

      // Adjust horizontal position to stay in viewport
      if (position.x + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 20;
      }
      if (adjustedX < 20) {
        adjustedX = 20;
      }

      // Adjust vertical position to stay in viewport
      if (position.y < rect.height + 20) {
        adjustedY = position.y + 80; // Position below instead
      }
      if (adjustedY + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 20;
      }

      setFormPosition({ x: adjustedX, y: adjustedY });
    }
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".drag-handle")) {
      setIsDragging(true);
      const rect = formRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setFormPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        setFormPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files).map((file) => ({
      file,
      uploading: true,
    }));

    setAttachments((prev) => [...prev, ...newFiles]);
    setIsUploading(true);
    startUpload(Array.from(files));
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return <Image className="h-4 w-4" />;
    if (type.startsWith("video/")) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = () => {
    if (content.trim() || attachments.length > 0 || drawing) {
      const finalAttachments = attachments
        .filter((att) => att.storageId)
        .map((att) => ({
          storageId: att.storageId as Id<"_storage">,
          name: att.file.name,
          type: att.file.type,
          size: att.file.size,
        }));

      onSubmit(
        content,
        finalAttachments.length > 0 ? finalAttachments : undefined,
        drawing || undefined,
      );
    }
  };

  const canSubmit =
    (content.trim() || attachments.length > 0 || drawing) &&
    !isUploading &&
    attachments.every((att) => !att.uploading);

  return (
    <div
      ref={formRef}
      className={`fixed z-50 bg-white dark:bg-gray-800 border border-border rounded-lg shadow-xl resize overflow-auto min-h-[200px] max-h-[700px] ${showDrawing
          ? "min-w-[800px] max-w-[900px]"
          : "min-w-[320px] max-w-[500px]"
        }`}
      style={{
        left: formPosition.x,
        top: formPosition.y,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Draggable header */}
      <div className="drag-handle cursor-move bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-t-lg border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary/60" />
          <span className="text-sm font-medium">
            {selectedRange
              ? `Comment at ${formatTime(selectedRange.start)} - ${formatTime(selectedRange.end)}`
              : `Comment at ${formatTime(timestamp)}`}
          </span>
          {selectedRange && (
            <Badge variant="outline" className="text-xs">
              Range: {formatDuration(selectedRange.end - selectedRange.start)}
            </Badge>
          )}
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>

      {/* Content area */}
      <div className="p-4 space-y-3">
        {/* Main content area - flex row when drawing is shown */}
        <div className={`${showDrawing ? "flex gap-4" : "space-y-3"}`}>
          {/* Textarea */}
          <div className={showDrawing ? "flex-1" : ""}>
            <Textarea
              placeholder="Add your feedback for this part of the video..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className={`resize-none ${showDrawing ? "h-[300px]" : "min-h-[80px]"
                }`}
              autoFocus
            />
          </div>

          {/* Drawing Section */}
          {showDrawing && (
            <div className="flex-shrink-0">
              <div className="text-sm font-medium text-primary mb-2">
                Draw on Video
              </div>
              <DrawingCanvas
                onSaveDrawing={(svgString) => {
                  setDrawing(svgString);
                  setShowDrawing(false);
                  toast({
                    title: "Drawing saved",
                    description: "Your drawing has been saved to the comment",
                  });
                }}
                onClearDrawing={() => {
                  setDrawing("");
                }}
                videoFrame={videoFrame}
                onHideDrawing={() => setShowDrawing(false)}
              />
            </div>
          )}
        </div>

        {/* Drawing Preview */}
        {drawing && !showDrawing && (
          <div className="text-xs text-green-600 flex items-center gap-2">
            <Brush className="h-3 w-3" />
            Drawing added to comment
          </div>
        )}

        {/* File attachments */}
        {attachments.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-primary/60">
              Attachments
            </div>
            {attachments.map((attachment, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border"
              >
                {getFileIcon(attachment.file.type)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {attachment.file.name}
                  </div>
                  <div className="text-xs text-primary/60">
                    {formatFileSize(attachment.file.size)}
                  </div>
                </div>
                {attachment.uploading ? (
                  <div className="flex items-center gap-1 text-xs text-primary/60">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Uploading...
                  </div>
                ) : (
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Paperclip className="h-4 w-4 mr-2" />
            Attach
          </Button>

          {!showDrawing && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDrawing(true)}
            >
              <Pen className="h-4 w-4 mr-2" />
              {drawing ? "Edit Drawing" : "Draw"}
            </Button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.txt"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />

          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            Add Comment
          </Button>

          {!showDrawing && (
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Resize handle */}
      <div className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-gray-300 dark:bg-gray-600 opacity-50 hover:opacity-75">
        <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-gray-500"></div>
      </div>
    </div>
  );
};
